"use client";

import { Bell, CircleCheck, FileText, Route, ShieldAlert, Truck, Wallet, type LucideIcon } from "lucide-react";
import { ArrowDownRight, ArrowUpRight, FileCheck2, Receipt, Sparkles } from "lucide-react";
import type { Dashboard, Notificacao } from "@/types";
import { useEmpresa, useSession } from "@/core/session";
import { useApiQuery } from "@/lib/query";
import { fmt } from "@/lib/format";
import { ToneIcon } from "@/components/status";
import { Skeleton } from "@/components/ui/controls";
import { useNav, type MTab } from "../nav";
import { openScreen } from "@/core/tabs";
import { cn } from "@/lib/utils";
import { AvatarEmpresa, Card, IconButton, List, Page, Row, Section } from "../ui";
import { ViagemCard } from "../cards";

/** Destino de navegação mobile para os alvos do painel. */
function irPara(alvo: { screen: string; params?: Record<string, string> }) {
  const nav = useNav.getState();
  const p = alvo.params ?? {};
  switch (alvo.screen) {
    case "viagem":
      return nav.push("viagem", { id: p.id });
    case "mdfe":
      return nav.push("mdfe", { id: p.id });
    case "ciot":
      return nav.push("ciot", { id: p.id });
    case "viagens":
      return nav.setTab("viagens", { filtro: p.periodo === "hoje" ? "hoje" : "todas" });
    case "ctes":
      return nav.setTab("documentos", { tipo: "cte" });
    case "mdfes":
      return nav.setTab("documentos", { tipo: "mdfe" });
    case "ciots":
      return nav.setTab("documentos", { tipo: "ciot" });
    default:
      return nav.setTab("documentos" as MTab);
  }
}

/** Ícone e cor de cada indicador do painel. */
const INDICADOR: Record<string, { icon: LucideIcon; cor: string }> = {
  viagens: { icon: Route, cor: "bg-primary-soft text-primary" },
  ctes: { icon: FileText, cor: "bg-info-soft text-info" },
  frete: { icon: Wallet, cor: "bg-success-soft text-success" },
  mdfe: { icon: Truck, cor: "bg-warning-soft text-warning" },
  averb: { icon: ShieldAlert, cor: "bg-danger-soft text-danger" },
  ciot: { icon: Receipt, cor: "bg-surface-2 text-muted" },
};

function Variacao({ v }: { v: number | null }) {
  if (v === null || !Number.isFinite(v) || v === 0) return null;
  const sobe = v > 0;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-xs font-medium tnum", sobe ? "text-success" : "text-danger")}>
      {sobe ? <ArrowUpRight className="size-3.5" /> : <ArrowDownRight className="size-3.5" />}
      {Math.abs(Math.round(v))}%
    </span>
  );
}

/** Conta nova: o que fazer antes da primeira viagem. */
function ComecePorAqui({ temCertificado }: { temCertificado: boolean }) {
  return (
    <Section title="Comece por aqui">
      <List>
        <Row
          leading={<Passo n={1} feito={temCertificado} />}
          title="Certificado digital A1"
          subtitle={temCertificado ? "Instalado" : "Necessário para emitir em produção"}
          onClick={() => openScreen("certificado")}
        />
        <Row leading={<Passo n={2} />} title="Veículos e motoristas" subtitle="Cadastre a frota e quem dirige" onClick={() => openScreen("veiculos")} />
        <Row leading={<Passo n={3} />} title="Primeira viagem" subtitle="Importe o XML da NF-e e emita tudo" onClick={() => openScreen("nova-viagem")} />
      </List>
    </Section>
  );
}

function Passo({ n, feito = false }: { n: number; feito?: boolean }) {
  return (
    <span className={cn("inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold", feito ? "bg-success-soft text-success" : "bg-primary-soft text-primary")}>
      {feito ? <FileCheck2 className="size-[18px]" /> : n}
    </span>
  );
}

export function MInicio() {
  const usuario = useSession((s) => s.usuario);
  const empresa = useEmpresa();
  const q = useApiQuery<Dashboard>("dashboard", "/dashboard", { refetchInterval: 60_000 });
  const notif = useApiQuery<Notificacao[]>("notificacoes", "/notificacoes");
  const naoLidas = (notif.data ?? []).filter((n) => !n.lida).length;
  const d = q.data;
  const primeiroNome = usuario?.nome.split(" ")[0] ?? "";

  const semViagens = !!d && d.ultimasViagens.length === 0;

  return (
    <Page
      leading={empresa ? <AvatarEmpresa empresa={empresa} className="size-10" /> : null}
      title={`Olá, ${primeiroNome}`}
      subtitle={empresa?.nomeFantasia}
      onRefresh={() => q.refetch()}
      actions={<IconButton icon={Bell} label="Notificações" badge={naoLidas} onClick={() => useNav.getState().push("notificacoes")} />}
    >
      <Section>
        <div className="grid grid-cols-2 gap-2.5">
          {(d?.indicadores ?? Array.from({ length: 6 }, () => null)).map((ind, i) =>
            ind ? (
              <Card key={ind.id} onClick={() => irPara(ind.alvo)} className="px-3.5 py-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs leading-4 text-muted">{ind.rotulo}</span>
                  {(() => {
                    const m = INDICADOR[ind.id] ?? { icon: Sparkles, cor: "bg-surface-2 text-muted" };
                    return (
                      <span className={cn("inline-flex size-7 shrink-0 items-center justify-center rounded-lg", m.cor)}>
                        <m.icon className="size-4" />
                      </span>
                    );
                  })()}
                </div>
                <div className="mt-1 flex items-baseline justify-between gap-2">
                  <span className="block truncate text-xl leading-7 font-semibold tnum">{ind.formato === "moeda" ? fmt.moneyShort(ind.valor) : fmt.int(ind.valor)}</span>
                  <Variacao v={ind.variacao} />
                </div>
              </Card>
            ) : (
              <div key={i} className="h-[74px] rounded-xl border border-border bg-surface p-3.5">
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="mt-3 h-5 w-14" />
              </div>
            ),
          )}
        </div>
      </Section>

      <Section title={`Pendências${d ? ` · ${d.pendencias.length}` : ""}`}>
        {!d ? (
          <Skeleton className="h-40 rounded-xl" />
        ) : d.pendencias.length === 0 ? (
          <Card className="flex items-center gap-3 px-4 py-3.5">
            <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-success-soft text-success">
              <CircleCheck className="size-5" />
            </span>
            <span>
              <span className="block text-[15px] font-medium">Tudo em dia</span>
              <span className="block text-[13px] text-muted">Nenhuma averbação, CIOT ou MDF-e pendente</span>
            </span>
          </Card>
        ) : (
          <List>
            {d.pendencias.slice(0, 8).map((p) => (
              <Row
                key={p.id}
                title={p.descricao}
                subtitle={`${p.tipo} · ${fmt.dayShort(p.data)}`}
                trailing={<ToneIcon tom={p.tom} />}
                onClick={["viagem", "mdfe", "ciot", "viagens", "ctes", "mdfes", "ciots"].includes(p.alvo.screen) ? () => irPara(p.alvo) : undefined}
              />
            ))}
          </List>
        )}
      </Section>

      {semViagens ? <ComecePorAqui temCertificado={!!empresa?.certificado} /> : null}

      <Section
        title="Últimas viagens"
        action={
          <button type="button" className="text-sm font-medium text-primary" onClick={() => useNav.getState().setTab("viagens")}>
            Ver todas
          </button>
        }
        className="pb-6"
      >
        <div className="flex flex-col gap-2.5">
          {d?.ultimasViagens.slice(0, 5).map((v) => (
            <ViagemCard key={v.id} v={v} onClick={() => useNav.getState().push("viagem", { id: v.id })} />
          ))}
          {semViagens ? (
            <Card className="flex items-center gap-3 px-4 py-3.5">
              <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-2 text-muted">
                <Route className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-medium">Nenhuma viagem ainda</span>
                <span className="block text-[13px] text-muted">A primeira aparece aqui assim que for emitida</span>
              </span>
              <button type="button" className="shrink-0 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-fg" onClick={() => openScreen("nova-viagem")}>
                Nova
              </button>
            </Card>
          ) : null}
        </div>
      </Section>
    </Page>
  );
}
